import { gql, useLazyQuery } from '@apollo/client';

import Button from '../../../components/Button';
import { useToast } from '../../../components/Toast';

import type { AdminGetDocumentQuery, AdminGetDocumentQueryVariables } from './__generated__/AttachmentDownloadLink.gql';

const ADMIN_GET_DOCUMENT = gql`
  query AdminGetDocument($documentId: ID!) {
    adminGetDocument(documentId: $documentId) {
      id
      name
      mimeType
      assetUrl
    }
  }
`;

/**
 * Decide what a click resolves to: open the presigned URL, or surface an
 * error toast when no URL came back (missing/expired row, failed request).
 * Pure so the open-vs-error branch and the label fallback are unit-testable
 * without rendering Apollo hooks. `documentId` is the toast fallback when the
 * attachment has no filename.
 */
export const resolveAttachmentOpen = (
  url: string | undefined,
  name: string | null,
  documentId: string
): { action: 'open'; url: string } | { action: 'error'; message: string } => {
  if (!url) {
    return { action: 'error', message: `Could not open attachment ${name ?? documentId}` };
  }
  return { action: 'open', url };
};

interface IAttachmentDownloadLinkProps {
  documentId: string;
  /** Original filename for the label; falls back to a generic label when null. */
  name: string | null;
}

/**
 * Renders one assistant-thread attachment as a button that opens the file in
 * a new tab. The presigned `assetUrl` is fetched lazily on click — never on
 * render — so scanning a transcript doesn't mint a URL per attachment. The
 * URL is short-lived, so the query is network-only to avoid serving a stale
 * (expired) cached URL on a later click.
 */
const AttachmentDownloadLink = ({ documentId, name }: IAttachmentDownloadLinkProps) => {
  const { showToast } = useToast();
  const [fetchDocument, { loading }] = useLazyQuery<AdminGetDocumentQuery, AdminGetDocumentQueryVariables>(
    ADMIN_GET_DOCUMENT,
    { fetchPolicy: 'network-only' }
  );

  const handleClick = async () => {
    // The Apollo errorLink already reports any GraphQL/network failure to
    // Sentry; the toast is operator-facing feedback. `network-only` execute
    // can either resolve with no data or reject depending on the error, so
    // cover both.
    let url: string | undefined;
    try {
      const { data } = await fetchDocument({ variables: { documentId } });
      url = data?.adminGetDocument.assetUrl;
    } catch {
      url = undefined;
    }
    const result = resolveAttachmentOpen(url, name, documentId);
    if (result.action === 'error') {
      showToast({ type: 'error', message: result.message });
      return;
    }
    window.open(result.url, '_blank', 'noopener');
  };

  return (
    <Button color="secondary" size="xs" isLoading={loading} onClick={() => void handleClick()}>
      📎 {name ?? 'attachment'}
    </Button>
  );
};

export default AttachmentDownloadLink;
